/**
 * Generates a complete color palette with 11 shades (50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950)
 * based on a base color (500 shade) using HSL lightness adjustments.
 *
 * @param baseColor - The base color in hex format (should be the 500 shade)
 * @returns Object with shade numbers as keys and hex color strings as values
 */
function generateColorShades(baseColor: string): Record<number, string> {
    // Remove # if present and convert to uppercase
    const cleanColor = baseColor.replace('#', '').toUpperCase()

    // Convert hex to RGB
    const hexToRgb = (hex: string): [number, number, number] => {
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        return [r, g, b]
    }

    // Convert RGB to hex
    const rgbToHex = (r: number, g: number, b: number): string => {
        const toHex = (n: number) =>
            Math.round(Math.max(0, Math.min(255, n)))
                .toString(16)
                .padStart(2, '0')
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
    }

    // Convert RGB to HSL
    const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
        r /= 255
        g /= 255
        b /= 255
        const max = Math.max(r, g, b)
        const min = Math.min(r, g, b)
        let h = 0, s = 0, l = (max + min) / 2

        if (max !== min) {
            const d = max - min
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0)
                    break
                case g:
                    h = (b - r) / d + 2
                    break
                case b:
                    h = (r - g) / d + 4
                    break
            }
            h /= 6
        }
        return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
    }

    // Convert HSL to RGB
    const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
        h /= 360
        s /= 100
        l /= 100
        let r = 0, g = 0, b = 0

        if (s === 0) {
            r = g = b = l // achromatic
        } else {
            const hue2rgb = (p: number, q: number, t: number) => {
                if (t < 0) t += 1
                if (t > 1) t -= 1
                if (t < 1 / 6) return p + (q - p) * 6 * t
                if (t < 1 / 2) return q
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
                return p
            }
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s
            const p = 2 * l - q
            r = hue2rgb(p, q, h + 1 / 3)
            g = hue2rgb(p, q, h)
            b = hue2rgb(p, q, h - 1 / 3)
        }
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
    }

    // Get base color RGB and HSL
    const [baseR, baseG, baseB] = hexToRgb(cleanColor)
    const [h, s, l] = rgbToHsl(baseR, baseG, baseB)

    // Define the shade keys
    const shadeKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]

    // Define the lightness values for each shade, centered on the base color's lightness for 500
    // These are relative offsets from the base color's lightness
    const lightnessOffsets: Record<number, number> = {
        50: 42,
        100: 34,
        200: 24,
        300: 14,
        400: 6,
        500: 0,
        600: -7,
        700: -17,
        800: -27,
        900: -37,
        950: -44
    }

    // Clamp a value between 0 and 100
    const clamp = (val: number) => Math.max(0, Math.min(100, val))

    // Generate all shades
    const result: Record<number, string> = {}
    for (const shade of shadeKeys) {
        if (shade === 500) {
            result[shade] = `#${cleanColor}`
        } else {
            // Optionally, slightly reduce saturation for lighter shades
            let adjS = s
            if (shade <= 200) {
                adjS = Math.max(20, s - 20)
            } else if (shade <= 400) {
                adjS = Math.max(40, s - 10)
            }
            const newL = clamp(l + lightnessOffsets[shade])
            const [r, g, b] = hslToRgb(h, adjS, newL)
            result[shade] = rgbToHex(r, g, b)
        }
    }
    return result
}

// Export the function
export default generateColorShades
